import type * as api from "@screencloud/billing-client-api";
import type {
  Billing,
  BillingV2CataloguePlan,
  BillingV2Intent,
} from "@screencloud/studio-billing-sdk/client";
import type {
  CalculateEstimateRequest,
  CalculateEstimateResponse,
} from "src/billinglatest/clients/service.client";
import type { Plan, TermUnit } from "src/billinglatest/types";
import { ROUTES } from "src/constants/routes";
import { getBillingV2Client } from "src/domains/billing/billingClient";
import { Device, type ShippingRate } from "../steps/CheckoutStep/shippingRates";
import type { PixiActivation, PixiBillingClient } from "./pixiBillingClient";

const V1_PLAN_ID = /^([a-z]+)-(month|year)-([a-z]{3})$/;

/**
 * Maps the flow's v1-style plan id to the billing v2 price id: `core-year-gbp` → `core-studio-year-gbp`.
 *
 * @remarks
 * An id that is already a v2 price id, or has another shape, is returned unchanged.
 */
export const toV2PriceId = (planId: string): string => {
  const match = V1_PLAN_ID.exec(planId);
  return match ? `${match[1]}-studio-${match[2]}-${match[3]}` : planId;
};

/**
 * Flattens the v2 catalogue into the flow's v1 plan rows, one per price, keyed `{group}-{cycle}-{currency}` so the
 * flow's `getPlan(group, cycle, currency)` finds them.
 */
export const toOnboardingPlans = (
  catalogue: BillingV2CataloguePlan[],
): Plan[] =>
  catalogue.flatMap((plan) => {
    const group = plan.id.replace(/-studio$/, "");
    return plan.prices.map((price) => ({
      id: `${group}-${price.periodUnit}-${price.currencyCode.toLowerCase()}`,
      name: plan.name,
      group,
      term: price.periodUnit as TermUnit,
      termNumber: price.period,
      price: price.unitPrice,
      currency: price.currencyCode.toUpperCase(),
      licenseCount: 1,
      version: "v4",
      pricingModel: "per_unit",
    }));
  });

const toCardSetup = (intent: BillingV2Intent) => ({
  id: intent.id ?? undefined,
  secret: intent.secret ?? undefined,
  status: intent.status ?? undefined,
});

/**
 * Prices the checkout with `me.estimate.checkout` and returns it in the v1 estimate shape the flow reads.
 */
const calculateEstimate = async (
  billing: Billing,
  request: CalculateEstimateRequest,
): Promise<CalculateEstimateResponse> => {
  const address = request.billingAddress;
  const summary = await billing.me.estimate.checkout({
    planId: toV2PriceId(request.planId),
    quantity: request.quantity,
    vatNumber: request.vatNumber,
    billingAddress: address?.country
      ? {
          country: address.country,
          stateCode: address.stateCode,
          zip: address.zip,
          line1: address.line1,
          line2: address.line2,
          city: address.city,
        }
      : undefined,
    option: request.option,
    shippingService: request.shippingService,
    shippingAddress: request.shippingAddress,
  });
  return {
    sub_total: summary.subTotal,
    total: summary.total,
    taxes: summary.taxes,
  };
};

/**
 * Ends the trial on billing v2.
 *
 * @remarks
 * With the PIXI bundle, `start` takes the price and quantity itself and adds the free device and shipping, so no
 * update is needed. Without it, `start` keeps the current plan, so the plan and licences are set first; a cancelled
 * subscription is reactivated first, because updating it would bill it at the catalogue price.
 */
const activate = async (
  billing: Billing,
  { planId, quantity, bundle }: PixiActivation,
) => {
  const priceId = toV2PriceId(planId);
  if (bundle) {
    await billing.me.subscription.start({
      option: "pixi",
      priceId,
      quantity,
      shippingAddress: bundle.shippingAddress,
      shippingService: bundle.shippingService,
    });
    return;
  }
  const { subscription } = await billing.me.get();
  if (subscription.status === "cancelled") {
    await billing.me.subscription.start();
    await billing.me.subscription.update({ priceId, quantity });
    return;
  }
  await billing.me.subscription.update({ priceId, quantity });
  await billing.me.subscription.start();
};

export interface V2PixiBillingClientOptions {
  /**
   * The subscription currency already in app context (`billingV2Subscription.currencyCode`, loaded at app start). When
   * given, the trialist's currency is read from it with no request.
   */
  currencyCode?: string | null;
}

/**
 * The PIXI flow on billing v2: the same steps as v1, through the billing v2 SDK.
 *
 * @remarks
 * The SDK client is created per call, so a refreshed session token is always used.
 */
export const createV2PixiBillingClient = (
  options: V2PixiBillingClientOptions = {},
): PixiBillingClient => {
  const sdk = () => getBillingV2Client();
  return {
    version: "v2",
    billingPath: ROUTES.ACCOUNT_V2,
    returnPaths: {
      paypal: `${ROUTES.ACCOUNT_V2}/complete/paypal`,
      direct_debit: `${ROUTES.ACCOUNT_V2}/complete/bank`,
    },

    getCurrency: async () => {
      if (options.currencyCode) {
        return options.currencyCode.toUpperCase();
      }
      // Not in context yet (should not happen once the billing version is known): ask billing.
      try {
        const { subscription } = await sdk().me.get();
        return subscription.currencyCode?.toUpperCase();
      } catch {
        return undefined;
      }
    },
    listPlans: async () => toOnboardingPlans(await sdk().plans.list()),
    // Rates come from the same `billing_pulse.hardware_shipping_rates` table as v1. The device is sent, as v1 does,
    // rather than relying on billing's PIXI default.
    listShippingRates: async () =>
      (await sdk().shippingRates.list(Device.Pixi)) as ShippingRate[],
    listDeviceCharges: async (itemId) =>
      (await sdk().charges.list({ itemId })).map((charge) => ({
        ...charge,
        currency: charge.currency.toUpperCase(),
      })) as api.Charges.Charge[],
    calculateEstimate: (request) => calculateEstimate(sdk(), request),
    verifyAddress: async (request) => {
      try {
        return await sdk().addresses.verify(request);
      } catch {
        // Same as v1: an unavailable verifier lets the address through.
        return { valid: true, issues: [] };
      }
    },

    updateBillingAddress: async (address) => {
      await sdk().me.customer.update({ address });
    },
    updateVatNumber: async (vatNumber) => {
      await sdk().me.customer.update({ vatNumber });
    },

    startCardSetup: async (paymentMethodId) =>
      toCardSetup(
        await sdk().me.paymentSources.card.setup.start({ paymentMethodId }),
      ),
    completeCardSetup: async (token) => {
      await sdk().me.paymentSources.card.setup.complete({ token });
    },
    authorizePaypal: async (urls) => {
      const response = await sdk().me.paymentSources.paypal.authorize(urls);
      return { redirectUrl: response.redirectUrl, token: response.token };
    },
    completePaypal: async (token) => {
      await sdk().me.paymentSources.paypal.complete({ token });
    },
    authorizeBank: async (urls) => {
      const response = await sdk().me.paymentSources.bank.authorize(urls);
      return {
        redirectUrl: response.redirectUrl,
        token: response.redirectFlowId,
      };
    },
    completeBank: async (token) => {
      await sdk().me.paymentSources.bank.complete({ redirectFlowId: token });
    },
    setInvoicePayment: async () => {
      await sdk().me.paymentSources.offline();
    },

    activate: (activation) => activate(sdk(), activation),
    updatePoNumber: async (poNumber) => {
      try {
        await sdk().me.subscription.update({ poNumber });
      } catch {
        // Non-fatal: the purchase is complete; the PO can be set later.
      }
    },
  };
};
